import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {userSettingsKeys} from '@/features/user-settings';
import {useApiClient} from '@/hooks';

import {trustAccountsApi} from '../api';

import type {TrustAccountCreatePayload, TrustAccountUpdatePayload} from '@sollapay/types';

export const trustAccountKeys = {
  all: ['trust-accounts'] as const,
  lists: () => [...trustAccountKeys.all, 'list'] as const,
  list: (filters: {status?: string}) => [...trustAccountKeys.lists(), filters] as const,
  details: () => [...trustAccountKeys.all, 'detail'] as const,
  detail: (id: string) => [...trustAccountKeys.details(), id] as const,
  balances: () => [...trustAccountKeys.all, 'balances'] as const,
  balance: (id: string) => [...trustAccountKeys.all, 'balance', id] as const,
  transactions: (id: string) => [...trustAccountKeys.all, 'transactions', id] as const,
  documents: (id: string) => [...trustAccountKeys.all, 'documents', id] as const,
  allMembers: () => [...trustAccountKeys.all, 'members'] as const,
  members: (id: string) => [...trustAccountKeys.allMembers(), id] as const,
  memberCandidates: (id: string) => [...trustAccountKeys.members(id), 'candidates'] as const,
  memberEvents: (id: string) => [...trustAccountKeys.members(id), 'events'] as const,
  guests: (id: string) => [...trustAccountKeys.all, 'guests', id] as const,
  guestEvents: (id: string) => [...trustAccountKeys.guests(id), 'events'] as const,
  suggestedTeam: () => [...trustAccountKeys.allMembers(), 'suggested-team'] as const,
  suggestedTeamExcluding: (excludeTrustAccountId?: string) =>
    [...trustAccountKeys.suggestedTeam(), {excludeTrustAccountId}] as const
};

export function useTrustAccountsQuery(status?: string) {
  const client = useApiClient();

  return useQuery({
    queryKey: trustAccountKeys.list({status}),
    queryFn: () => trustAccountsApi.fetchTrustAccounts(client, {status}),
    select: response => response.data,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  });
}

export function useTrustAccountQuery(id: string) {
  const client = useApiClient();

  return useQuery({
    queryKey: trustAccountKeys.detail(id),
    queryFn: () => trustAccountsApi.fetchTrustAccount(client, id),
    enabled: !!id,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    select: response => response.data
  });
}
/**
 * Hook to create a new trust account draft
 * Invalidates the trust accounts list query on success, and the user settings query
 * when consent was submitted (first trust account) so the in-session cache reflects it.
 */
export function useCreateTrustAccountMutation() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TrustAccountCreatePayload) =>
      trustAccountsApi.createTrustAccount(client, payload),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({
        queryKey: trustAccountKeys.lists()
      });
      if (payload.members?.length) {
        queryClient.invalidateQueries({queryKey: trustAccountKeys.suggestedTeam()});
      }
      if (payload.marketingConsent !== undefined) {
        queryClient.invalidateQueries({
          queryKey: userSettingsKeys.all
        });
      }
    }
  });
}

/**
 * Hook to update a trust account
 * Invalidates both the trust accounts list and the specific detail query on success
 */
export function useUpdateTrustAccountMutation(id: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TrustAccountUpdatePayload) =>
      trustAccountsApi.updateTrustAccount(client, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trustAccountKeys.detail(id)
      });
      queryClient.invalidateQueries({
        queryKey: trustAccountKeys.lists()
      });
    }
  });
}

/**
 * Hook to delete a trust account
 * Invalidates trust account list, balances, and specific detail cache on success
 */
export function useDeleteTrustAccountMutation() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => trustAccountsApi.deleteTrustAccount(client, id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: trustAccountKeys.detail(id)
      });
      queryClient.invalidateQueries({
        queryKey: trustAccountKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: trustAccountKeys.balances()
      });
    }
  });
}
